import axios from 'axios';
import Link from 'next/link';
import styled from 'styled-components';
import { useMemo } from 'react';
import useSWRImmutable from 'swr/immutable';

import { IFeaturedInCollection } from 'productTemplate/types/FeaturedInCollection';
import { RelatedProducts } from 'productTemplate/components/relatedProducts/RelatedProducts';
import { t } from 'localization/utils/t';
import { variableFontRegular } from 'styles/theme';

const TP = 'productTemplate.components.windows.FactsWindow';

const swrFetcher = (options) => {
  return axios
    .get(options.url, {
      params: options.data,
    })
    .then((res) => res.data?.response?.results);
};

export const FeaturedInProducts = ({
  featuredInCollections,
  productTemplateSlug,
  productTemplateId,
}: {
  featuredInCollections: IFeaturedInCollection[];
  productTemplateSlug: string;
  productTemplateId: number;
}) => {
  const fetchCollectionData = useMemo(() => {
    return featuredInCollections?.map((collection) => ({
      url: collection ? '/web-api/consumer-search/collection-content' : null,
      data: {
        slug: collection?.slug,
        pageLimit: 8,
        id: collection?.recordId,
        excludeProductIdsList: productTemplateId.toString(),
      },
    }));
  }, [featuredInCollections]);

  const collectionResults = useSWRImmutable(fetchCollectionData, (urls) => {
    return Promise.all(urls.map((url) => swrFetcher(url)));
  });

  if (collectionResults.error) {
    return null;
  }

  if (!collectionResults.data) {
    return (
      <>
        {featuredInCollections.map((featuredInCollection) => (
          <FeaturedInProducts.Title key={featuredInCollection.slug}>
            <Link href={`/collections/${featuredInCollection.slug}`}>
              {`${t(`${TP}.featuredIn`, 'Featured In')}: ${featuredInCollection.title}`}
            </Link>
          </FeaturedInProducts.Title>
        ))}
      </>
    );
  }

  return (
    <>
      {collectionResults.data?.map((result, index) => {
        if (result.length === 0) {
          return null;
        }
        const currCollection = featuredInCollections[index];
        const currCollectionTitle = currCollection?.title?.toUpperCase();
        return (
          <RelatedProducts
            productTemplateSlug={productTemplateSlug}
            key={currCollectionTitle}
            title={`${t(`${TP}.featuredIn`, 'Featured In')}${
              currCollectionTitle ? `: ${currCollectionTitle}` : null
            }`}
            titleLink={currCollection?.slug ? `/collections/${currCollection?.slug}` : null}
            titleTag="h3"
            products={result}
            section="featured_in"
          />
        );
      })}
    </>
  );
};

FeaturedInProducts.Title = styled.h3`
  ${variableFontRegular({ size: '11.5px' })};
  margin: 16px;
  text-transform: capitalize;

  a {
    text-decoration: underline;

    &:hover {
      text-decoration: underline;
    }
  }
`;
